<script>
import { v4 as uuidv4 } from "uuid";
import {
    modalState,
    showModal,
    hideModal,
} from "@app/store/modalStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { uiState } from "@app/store/uiStore.svelte.js";
import { externalDataState } from "@app/store/externalDataStore.svelte.js";
import { updateDuration } from "@app/store/playerStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { projectState } from "@app/store/projectStore.svelte.js";
import { establishHistoryBaseline } from "@app/store/historyStore.svelte.js";
import { fade } from "svelte/transition";
import { onDestroy, onMount } from "svelte";
import { BarLoader } from "svelte-loading-spinners";
import * as m from "@app/paraglide/messages.js";
import { startProxyRt, stopProxyRt } from "@app/lib/proxyRt.js";
import { applyTeamCommit } from "@app/lib/applyTeamCommit.js";
/* Firebase */
import { auth, functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";

/* CC LIB */
import tcLib from "@cclib/lib/timecode.js";
import frameRates from "@cclib/dict/frameRates.js";

const uid = uuidv4();

let shareCode = $state(""),
    syncing = $state(false),
    statusMsg = $state(m.team_project_import_status_pulling_latest_commit()),
    audioExtractionComplete = false,
    manifestFileExists = false,
    audioExtractionWorker = environment.electron
        ? new Worker("./build/workers/audioExtraction.js")
        : null,
    audioWaveformPath,
    audioOutputPath,
    ffmpegRes;

onMount(async () => {
    if (externalDataState.shareCode && externalDataState.status === "importing") {
        /* Handed over by a "closedcaptioncreator://share" deep link, where the
           window's own URL is a local file with no params to read. Consume it
           so a later open from the Storage Explorer - which pushes the share id
           into the URL instead - isn't shadowed by this stale value. */
        shareCode = externalDataState.shareCode;
        delete externalDataState.shareCode;
        delete externalDataState.status;
    } else {
        // console.info("DEBUG: Parsing URL params from URL: " + window.location.href);
        let urlString = window.location.href;
        let url = new URL(urlString);
        shareCode = url.searchParams.get("id") || "";
        // console.info("DEBUG: Share code from URL: " + shareCode);
    }
});

onDestroy(async () => {
    if (audioExtractionWorker) {
        audioExtractionWorker.terminate();
    }
});

async function getFileMetadata(filePath, ffmpegFullPath) {
    try {
        const exec = window.exec;
        return await exec(
            `"${ffmpegFullPath.replace("app.asar", "app.asar.unpacked")}" -y -i "${filePath}"`,
        );
    } catch (err) {
        return err.message;
    }
}

function getMediaIncode(ffmpegOutput) {
    let matches = ffmpegOutput.match(
        /(?<=timecode\s+:\s+)\d\d:\d\d:\d\d(:|;)\d\d/,
    );
    let mediaIncode = matches ? matches[0].replace(";", ":") : false;

    console.log("Media incode detected: " + mediaIncode);
    return mediaIncode == "null" ? false : mediaIncode;
}

function getMediaFrameRate(ffmpegOutput) {
    // console.log("GETTING MEDIA FRAME RATE");
    //console.log("FFMPEG OUTPUT:");
    //console.log(ffmpegOutput);

    let matches = ffmpegOutput.match(/\d+\.\d+(?= fps)|\d+(?= fps)/);
    let mediaFrameRate = matches ? matches[0] : false;

    console.log("Media frame rate detected: " + mediaFrameRate);
    let fr = frameRates.frameRateMapping[mediaFrameRate];
    return fr;
}

function getMediaDuration(ffmpegOutput) {
    /*  console.log("GETTING MEDIA DURATION");
        console.log("FFMPEG OUTPUT:");
        console.log(ffmpegOutput); */
    let matches = ffmpegOutput.match(/(?<=Duration:\s)\d\d:\d\d:\d\d\.\d\d/);
    let mediaDuration = matches ? matches[0] : false;

    console.log(mediaDuration);
    if (mediaDuration) {
        mediaDuration = tcLib.tcMsToSec(mediaDuration);
        console.log("media duration in seconds: " + mediaDuration);
    }

    return mediaDuration;
}

function checkFileExistsSync(filePath) {
    try {
        fsSync.accessSync(filePath);
        return true;
    } catch (error) {
        return false;
    }
}

async function importProject() {
    /* rkeMvkZOeDPNZrlyjdEIrJeEs0P2-06e590bf-22ce-4902-9bbf-d0e100dd2208 */
    toast.push(m.team_project_import_starting_info(), {
        classes: ["toast-info"],
    });

    try {
        if (!shareCode) {
            throw new Error(m.team_project_import_share_code_required());
        }

        syncing = true;
        let importRes = await httpsCallable(functions, "v8ImportTeamProjectV3")({ id: shareCode.trim() });
        console.log(importRes);
        const payload = importRes.data;
        let commitFileJson;
        if (payload?.commit?.filteredCommit) {
            commitFileJson = payload.commit.filteredCommit;
        } else if (payload?.commit?.signedFileUrl) {
            const signedFileUrl = payload.commit.signedFileUrl;
            const commitUrl = Array.isArray(signedFileUrl) ? signedFileUrl[0] : signedFileUrl;
            let commitFileRes = await fetch(commitUrl);
            if (!commitFileRes.ok) {
                throw new Error(`Failed to download commit (${commitFileRes.status})`);
            }
            commitFileJson = await commitFileRes.json();
        } else if (Array.isArray(payload) && payload[0]) {
            let commitFileRes = await fetch(payload[0]);
            if (!commitFileRes.ok) {
                throw new Error(`Failed to download commit (${commitFileRes.status})`);
            }
            commitFileJson = await commitFileRes.json();
        } else {
            throw new Error("No commit file returned.");
        }
        console.log("COMMIT FILE JSON:", commitFileJson);

        statusMsg = m.team_project_import_status_loading_commit();

        await applyTeamCommit(commitFileJson, { replaceAll: true, applyIdentity: true });
        projectState.username = auth.currentUser.email;
        projectState.name = commitFileJson.name || m.team_project_import_untitled_name();

        establishHistoryBaseline({ name: "import project" });

        statusMsg = m.team_project_import_status_done();
        toast.push(m.team_project_import_complete(), {
            classes: ["toast-success"],
        });

        if (
            ((commitFileJson.media.storage === "Local Storage" ||
                commitFileJson.media.storage === "Proxy RT") &&
                !environment.electron) ||
            commitFileJson.media.localPath === ""
        ) {
            player.src = '';
            showModal("mediaImport");
        } else {
            importMedia();
        }
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.team_project_import_failed({ message: err.message }), {
            classes: ["toast-danger"],
        });
        hideModal();
    }
}

async function importMedia() {
    try {
        updateDuration(false);
        uiState.timeline = false;
        player.src = '';

        /* Kill any Proxy RT session from previously loaded media. */
        if (environment.electron) {
            stopProxyRt();
        }
        let mediaType = "video/mp4";
        switch (projectState.media.storage) {
            case "Vimeo":
                player.src = projectState.media.path;
                manifestFileExists = true;
                audioExtractionComplete = true;
                projectState.media.useFallback = true;
                break;
            case "YouTube":
            case "Mux":
            case "DASH Manifest":
            case "Cloudflare Stream":
                player.src = projectState.media.path;
                manifestFileExists = true;
                audioExtractionComplete = true;

                projectState.media.useFallback = true;
                break;
            case "HLS Manifest":
                audioExtractionComplete = true;
                player.src = projectState.media.path;
                projectState.media.useFallback = true;
                break;
            case "Proxy RT":
                if (!checkFileExistsSync(projectState.media.localPath)) {
                    toast.push(
                        m.team_project_import_media_not_found(),
                        { classes: ["toast-danger"] },
                    );
                    showModal("mediaImport");
                    return;
                }

                audioOutputPath = os.tmpdir() + path.sep + uid + ".json";

                ffmpegRes = await getFileMetadata(
                    projectState.media.localPath,
                    window.ffmpegPath
                );
                projectState.media.info = {
                    incode: getMediaIncode(ffmpegRes),
                    frameRate: getMediaFrameRate(ffmpegRes),
                    duration: getMediaDuration(ffmpegRes),
                };

                audioExtractionWorker.postMessage({
                    inputPath: projectState.media.localPath,
                    outputPath: audioOutputPath,
                    ffmpegPath: window.ffmpegPath,
                    duration: projectState.media.info.duration,
                });

                audioExtractionWorker.onmessage = (msg) => {
                    // console.log(msg);
                    if (msg.data.status === "in_progress") {
                        console.log(msg.data.result);
                    } else {
                        projectState.media.peaksPath = msg.data.error
                            ? ""
                            : audioOutputPath;
                        projectState.media.useFallback = false;

                        toast.push(
                            `${msg.data.error ? msg.data.error : m.team_project_import_audio_extraction_complete()}`,
                            {
                                classes: [
                                    `toast-${msg.data.error ? "danger" : "success"}`,
                                ],
                            },
                        );

                        audioExtractionComplete = true;
                        closeModal();
                    }
                };
                
                try {
                    const proxySession = await startProxyRt({
                        inputPath: projectState.media.localPath,
                        duration: projectState.media.info.duration,
                        onProgress: (progress) => {
                            statusMsg = `Generating Proxy Stream: ${progress.percent}% (${progress.segmentsCompleted}/${progress.totalSegments} segments)`;
                        },
                    });

                    projectState.media.path = proxySession.url;
                    manifestFileExists = true;
                    player.src = proxySession.url;
                    closeModal();
                } catch (err) {
                    manifestFileExists = true;
                    toast.push(`${err.message}`, {
                        classes: ["toast-danger"],
                    });

                    closeModal();
                }

                break;
            case "Cloud Storage":
                /* Cloud Storage*/
                if (projectState.media.type === "video/quicktime") {
                    mediaType = "video/mp4";
                } else if (projectState.media.type === "video/x-matroska") {
                    mediaType = "video/mkv";
                } else {
                    mediaType = projectState.media.type;
                }

                //Get file size of url
                let fileSize = await getFileSize(projectState.media.path);
                if (fileSize) {
                    const sizeInBytes = parseInt(fileSize);
                    const sizeInKilobytes = sizeInBytes / 1024;
                    const sizeInMegabytes = sizeInKilobytes / 1024;
                    if (sizeInMegabytes < 512) {
                        projectState.media.useFallback = false;
                    }
                }

                player.src = { src: projectState.media.path, type: mediaType };

                /* Update Flags */
                manifestFileExists = true;
                audioExtractionComplete = true;

                break;
            default:
                /* Local Storage */
                if (!checkFileExistsSync(projectState.media.localPath)) {
                    toast.push(
                        m.team_project_import_media_not_found(),
                        { classes: ["toast-danger"] },
                    );
                    showModal("mediaImport");
                    return;
                }
                audioOutputPath = os.tmpdir() + path.sep + uid + ".json";
                audioWaveformPath = replaceFilename(
                    window.ffmpegPath,
                    environment.windows ? "audiowaveform.exe" : "audiowaveform",
                );

                manifestFileExists = true;
                mediaType =
                    !projectState.media.type ||
                    projectState.media.type === "video/quicktime"
                        ? "video/mp4"
                        : projectState.media.type;
                
                player.src = {
                    src: projectState.media.localPath,
                    type: mediaType,
                };
                ffmpegRes = await getFileMetadata(
                    projectState.media.localPath,
                    window.ffmpegPath
                );
                projectState.media.info = {
                    incode: getMediaIncode(ffmpegRes),
                    frameRate: getMediaFrameRate(ffmpegRes),
                    duration: getMediaDuration(ffmpegRes),
                };

                audioExtractionWorker.postMessage({
                    inputPath: projectState.media.localPath,
                    outputPath: audioOutputPath,
                    ffmpegPath: window.ffmpegPath,
                    duration: projectState.media.info.duration,
                });

                audioExtractionWorker.onmessage = (msg) => {
                    // console.log(msg);
                    if (msg.data.status === "in_progress") {
                        //statusMsg = msg.data.result;
                    } else if (msg.data.error){
                        projectState.media.peaksData = [[0],[0]];
                        audioExtractionComplete = true;
                        closeModal();
                    } else {
                        projectState.media.peaksPath = msg.data.error
                            ? ""
                            : audioOutputPath;
                        projectState.media.useFallback = false;

                        toast.push(
                            `${msg.data.error ? msg.data.error : m.team_project_import_media_import_complete()}`,
                            {
                                classes: [
                                    `toast-${msg.data.error ? "danger" : "success"}`,
                                ],
                            },
                        );

                        audioExtractionComplete = true;
                        closeModal();
                    }
                };
        }

        closeModal();
    } catch (err) {
        toast.push(m.team_project_import_media_import_failed(), {
            classes: ["toast-danger"],
        });

        showModal("mediaImport");
    }
}

function closeModal() {
    console.log(
        "Close Modal Called",
        audioExtractionComplete,
        manifestFileExists,
    );
    if (audioExtractionComplete && manifestFileExists) {
        statusMsg = m.team_project_import_status_project_complete();

        setTimeout(() => {
            uiState.timeline = true;
            hideModal();
        }, 2500);
    }
}

async function getFileSize() {
    try {
        const response = await fetch(projectState.media.path, {
            method: "HEAD",
        });
        const fileSize = response.headers.get("content-length");
        return fileSize;
    } catch (err) {
        return false;
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'teamProjectImport'
        ? 'show d-block'
        : ''}"
    role="dialog"
    aria-labelledby="teamProjectImportTitle"
    aria-modal="true"
    tabindex="-1"
    id="TeamProjectImport">
    <div
        class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="teamProjectImportTitle">
                    {m.team_project_import_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.close()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <form>
                    <div class="mb-3">
                        <label class="form-label" for="shareCodeInput"
                            >{m.team_project_import_share_code_label()}</label>
                        <input
                            type="text"
                            class="form-control"
                            id="shareCodeInput"
                            bind:value={shareCode}
                            aria-describedby="shareCodeHelp" />
                        <small id="shareCodeHelp" class="form-text text-muted"
                            >{m.team_project_import_share_code_example()}</small>
                    </div>
                    {#if syncing}
                        <div class="loading-status-container text-center my-4" aria-live="polite" role="status">
                            <div class="loader-container d-flex justify-content-center mb-3">
                                <BarLoader
                                    size="300"
                                    color="#1eb4b2"
                                    unit="px"
                                    duration="3s"></BarLoader>
                            </div>
                            <p class="status-message fw-bold text-secondary mb-0">{statusMsg}</p>
                        </div>
                    {/if}
                    <button
                        type="button"
                        class="btn btn-primary float-end"
                        id="importProjectBtn"
                        onclick={importProject}
                        disabled={!shareCode || syncing}
                        aria-label={m.team_project_import_start_aria_label()}
                        >{m.team_project_import_button()}</button>
                </form>
            </div>
        </div>
    </div>
</div>
